'use client';

import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { getInitials as resolveInitials } from '@/lib/displayName';

export type AvatarStatus = 'online' | 'busy' | 'away' | 'offline';
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  src?: string | null;
  /** Display name or username — used to derive initials and the deterministic color. Required. */
  name: string;
  /** Size token or explicit pixel diameter. Defaults to 'md' (40px). */
  size?: AvatarSize | number;
  status?: AvatarStatus;
  /** Convenience for the common "currently online" badge — equivalent to status="online". */
  online?: boolean;
  className?: string;
  /**
   * When provided, wraps the avatar in a Link to /u/{username} with a hover
   * ring. Pass null/undefined for orphan or anonymous users — those render
   * as a non-clickable avatar.
   */
  username?: string | null;
  /** Rounded-square shape (radius = size × 0.24) instead of a circle. */
  square?: boolean;
  /** Overlay a 1px --border-strong hairline ring (used by the profile redesign). */
  ring?: boolean;
}

const SIZE_PX: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
};

const STATUS_BG: Record<AvatarStatus, string> = {
  online: 'bg-status-online',
  busy: 'bg-status-busy',
  away: 'bg-status-away',
  offline: 'bg-status-offline',
};

// Eight visually distinct backgrounds that all read on dark surfaces. White
// foreground is always used for initials.
const COLORS = [
  'bg-accent',
  'bg-status-online',
  'bg-status-busy',
  'bg-status-away',
  'bg-purple-600',
  'bg-pink-600',
  'bg-cyan-600',
  'bg-orange-600',
];

// Initials extraction is centralized in src/lib/displayName.ts so non-Latin
// names ("횀 레", "張偉") aren't stripped into "?" by the local regex.
const getInitials = resolveInitials;

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i)) % COLORS.length;
  }
  return COLORS[hash];
}

function resolveSize(size: AvatarSize | number | undefined): number {
  if (typeof size === 'number') return size;
  return SIZE_PX[size ?? 'md'];
}

function dotSizeFor(diameter: number): number {
  if (diameter <= 24) return 6;
  if (diameter <= 40) return 8;
  if (diameter <= 56) return 10;
  return 14;
}

export function Avatar({
  src,
  name,
  size = 'md',
  status,
  online,
  className,
  username,
  square = false,
  ring = false,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const px = resolveSize(size);
  const initials = getInitials(name);
  const bgClass = getAvatarColor(name);
  const showImage = !!src && !imgFailed;
  const effectiveStatus: AvatarStatus | undefined = online ? 'online' : status;
  const dotPx = dotSizeFor(px);
  // Rounded-square radius per the design system (size × 0.24). Inline radius
  // overrides the rounded-full utility classes below when square.
  const squareRadius = square ? Math.max(6, Math.round(px * 0.24)) : undefined;

  // Reset failure state if src changes (e.g. user uploads a new photo).
  React.useEffect(() => {
    setImgFailed(false);
  }, [src]);

  const fontSize = Math.max(10, Math.round(px * 0.4));

  const inner = (
    <>
      <div
        className="overflow-hidden rounded-full w-full h-full"
        style={square ? { borderRadius: squareRadius } : undefined}
      >
        {showImage ? (
          <img
            src={src as string}
            alt={name}
            className="w-full h-full object-cover block"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div
            className={cn(
              'w-full h-full flex items-center justify-center select-none font-semibold uppercase tracking-wide text-white',
              bgClass
            )}
            style={{ fontSize, letterSpacing: '0.02em' }}
            aria-hidden="true"
          >
            {initials}
          </div>
        )}
      </div>
      {effectiveStatus && (
        <span
          className={cn(
            'absolute rounded-full ring-2 ring-surface-0',
            STATUS_BG[effectiveStatus]
          )}
          style={{
            width: dotPx,
            height: dotPx,
            right: 0,
            bottom: 0,
          }}
          aria-hidden="true"
        />
      )}
      {ring && (
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: square ? squareRadius : 9999,
            border: '1px solid var(--border-strong)',
          }}
        />
      )}
    </>
  );

  const wrapperStyle: React.CSSProperties = {
    width: px,
    height: px,
    aspectRatio: '1 / 1',
    ...(square ? { borderRadius: squareRadius } : {}),
  };

  if (username && username.trim()) {
    return (
      <Link
        href={`/u/${encodeURIComponent(username)}`}
        prefetch={false}
        aria-label={`View ${name}'s profile`}
        className={cn(
          'relative shrink-0 rounded-full block',
          'transition-shadow duration-150 hover:ring-2 hover:ring-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          className
        )}
        style={wrapperStyle}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={wrapperStyle}
    >
      {inner}
    </div>
  );
}
